// src/pages/Officiels/OfficielsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { FaFilter, FaUsers } from "react-icons/fa";
import { getAllOfficielsList } from "../../services/acteursServices";
import { getGroupsList } from "../../services/groupServices";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";

const OfficielsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [officiels, setOfficiels] = useState([]);
  const [displayedOfficiels, setDisplayedOfficiels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");

  // Liste des groupes présents réellement dans les officiels affichés
  const [filteredGroups, setFilteredGroups] = useState([]);

  // Optionnel : si tu veux plus tard croiser avec tous les groupes existants
  const [groups, setGroups] = useState([]);

  /* ===== Helpers style ===== */
  const getTitreBadgeClass = (titre) => {
    const t = (titre || "").toLowerCase();
    if (t.includes("international")) return "bg-purple-100 text-purple-800 ring-purple-200";
    if (t.includes("national")) return "bg-red-100 text-red-800 ring-red-200";
    if (t.includes("ligue") || t.includes("régional") || t.includes("regional"))
      return "bg-blue-100 text-blue-800 ring-blue-200";
    if (t.includes("département") || t.includes("departement"))
      return "bg-emerald-100 text-emerald-800 ring-emerald-200";
    if (t.includes("cs") || t.includes("commissaire"))
      return "bg-amber-100 text-amber-800 ring-amber-200";
    return "bg-slate-100 text-slate-800 ring-slate-200";
  };

  const getGroupPillClass = (group) => {
    const base = "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold mr-1 mb-1 ring-1";
    const color = group?.color_groupe || "bg-slate-100 text-slate-800";
    return `${base} ${color}`;
  };

  /* ===== Fetch data ===== */
  const fetchOfficiels = async () => {
    try {
      const officielsData = await getAllOfficielsList();
      setOfficiels(Array.isArray(officielsData) ? officielsData : []);
      setDisplayedOfficiels(Array.isArray(officielsData) ? officielsData : []);
      updateFilteredGroups(officielsData);
    } catch (error) {
      console.error("Erreur lors du chargement des officiels:", error);
      toast.error("Erreur lors du chargement des officiels");
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsData = await getGroupsList();
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (error) {
      console.error("Erreur lors du chargement des groupes:", error);
      toast.error("Erreur lors du chargement des groupes");
    }
  };

  useEffect(() => {
    fetchOfficiels();
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Filtres ===== */

  const updateFilteredGroups = (officielsList) => {
    const groupsSet = new Set();
    (officielsList || []).forEach((officiel) => {
      (officiel.Groups || []).forEach((group) => {
        if (group?.nom) groupsSet.add(group.nom);
      });
    });
    setFilteredGroups(Array.from(groupsSet).sort());
  };

  const filterOfficiels = (term, group, titre) => {
    let list = officiels;

    if (term) {
      const lower = term.toLowerCase();
      list = list.filter(
        (o) =>
          o.nom.toLowerCase().includes(lower) ||
          o.prenom.toLowerCase().includes(lower) ||
          (o.titre_arbitrage || "").toLowerCase().includes(lower)
      );
    }

    if (group) {
      list = list.filter((o) =>
        (o.Groups || []).some((g) => g.nom === group)
      );
    }

    if (titre) {
      list = list.filter(
        (o) => (o.titre_arbitrage || "") === titre
      );
    }

    setDisplayedOfficiels(list);
    updateFilteredGroups(list);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterOfficiels(term, selectedGroup, selectedTitle);
  };

  const handleGroupChange = (e) => {
    const value = e.target.value;
    setSelectedGroup(value);
    filterOfficiels(searchTerm, value, selectedTitle);
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setSelectedTitle(value);
    filterOfficiels(searchTerm, selectedGroup, value);
  };

  /* ===== Options titre d’arbitrage ===== */
  const titreOptions = useMemo(() => {
    const set = new Set();
    officiels.forEach((o) => {
      if (o.titre_arbitrage) set.add(o.titre_arbitrage);
    });
    return Array.from(set).sort();
  }, [officiels]);

  /* ===== Stats ===== */
  const stats = useMemo(() => {
    const total = displayedOfficiels.length;
    const arbitres = displayedOfficiels.filter((o) =>
      (o.titre_arbitrage || "").toLowerCase().includes("arbitre")
    ).length;
    const commissaires = displayedOfficiels.filter((o) =>
      (o.titre_arbitrage || "").toLowerCase().includes("commissaire")
    ).length;
    return { total, arbitres, commissaires };
  }, [displayedOfficiels]);

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* TITRES PAGE */}
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        OFFICIELS DU COMITÉ
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-6 font-montserrat uppercase">
        Arbitres & Commissaires Sportifs
      </h2>

      {/* BLOC FILTRES + STATS */}
      <div className="w-full max-w-[1280px] mx-auto mb-5 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/70 backdrop-blur">
        {/* Bandeau haut */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-4 sm:px-6 py-4 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.3em] opacity-80">
                BASE OFFICIELS
              </p>
              <p className="text-sm sm:text-base mt-1 flex items-center gap-2">
                <FaUsers />
                <span>
                  Suivez les arbitres & commissaires du comité, filtrés par
                  groupe et titre d’arbitrage.
                </span>
              </p>
            </div>

            {/* Stats rapides */}
            <div className="flex flex-wrap justify-start sm:justify-end gap-2 text-[11px] sm:text-xs">
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
                <span className="font-semibold">Total</span>
                <span className="text-slate-600">({stats.total})</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-emerald-200">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span>Arbitres</span>
                <span className="text-slate-600">({stats.arbitres})</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-amber-200">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                <span>Commissaires</span>
                <span className="text-slate-600">({stats.commissaires})</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="px-4 sm:px-6 py-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            {/* Recherche */}
            <div className="flex-1">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Recherche (nom, prénom, titre…)
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                  <FaFilter />
                </span>
                <input
                  className="input input-bordered w-full pl-8 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  type="text"
                  placeholder="Rechercher un officiel…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* Filtre groupe */}
            <div className="w-full md:w-1/3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Groupe
              </label>
              <select
                className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                value={selectedGroup}
                onChange={handleGroupChange}
              >
                <option value="">Tous les groupes</option>
                {filteredGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre titre arbitrage */}
            <div className="w-full md:w-1/3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Titre d’arbitrage
              </label>
              <select
                className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                value={selectedTitle}
                onChange={handleTitleChange}
              >
                <option value="">Tous les titres</option>
                {titreOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Compteur global (sous le bloc filtre) */}
      <div className="text-center mb-4">
        <p className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-100">
          {displayedOfficiels.length} officiel(s) trouvé(s)
        </p>
      </div>

      {/* TABLE OFFICIELS */}
      <div className="overflow-x-auto shadow-xl">
        <table className="table bg-white dark:bg-slate-300 font-montserrat max-w-[1280px] mx-auto border-collapse border-2 border-slate-200 dark:border-slate-600 mb-8">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                Photo
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                Nom Prénom
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[320px]">
                Groupes (R = Responsable)
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                Titre d’arbitrage
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedOfficiels.map((officiel) => (
              <tr
                key={officiel.id}
                className="text-left hover:bg-slate-50 dark:hover:bg-slate-200/60 transition-colors"
              >
                {/* Avatar */}
                <td className="px-4 py-3 align-top">
                  <img
                    className="rounded-full w-12 h-12 object-cover ring-1 ring-slate-200"
                    src={officiel.photo_url || "/img/user_avatar.jpg"}
                    alt={`${officiel.prenom} ${officiel.nom}`}
                  />
                </td>

                {/* Nom / prénom */}
                <td className="px-4 py-3 align-top">
                  <div className="text-sm font-semibold text-slate-900">
                    {(officiel.nom || "").toUpperCase()} {officiel.prenom || ""}
                  </div>
                  {officiel.club_acteur && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {officiel.club_acteur}
                    </div>
                  )}
                </td>

                {/* Groupes */}
                <td className="px-4 py-3 align-top text-sm">
                  {officiel.Groups && officiel.Groups.length > 0 ? (
                    <div className="flex flex-wrap">
                      {officiel.Groups.map((group) => (
                        <div
                          key={group.id}
                          className={getGroupPillClass(group)}
                        >
                          {group.ActeurGroup?.is_responsable && (
                            <span className="mr-1 text-[10px] font-black">
                              R
                            </span>
                          )}
                          <span>{group.nom}</span>
                          {group.ActeurGroup?.poste && (
                            <span className="ml-1 text-[10px] opacity-80 italic">
                              ({group.ActeurGroup.poste})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">Aucun groupe</span>
                  )}
                </td>

                {/* Titre arbitrage */}
                <td className="px-4 py-3 align-top">
                  {officiel.titre_arbitrage ? (
                    <span
                      className={
                        "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 " +
                        getTitreBadgeClass(officiel.titre_arbitrage)
                      }
                    >
                      {officiel.titre_arbitrage}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Non renseigné</span>
                  )}
                </td>
              </tr>
            ))}
            {displayedOfficiels.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-slate-500"
                >
                  Aucun officiel ne correspond à vos filtres.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

export default OfficielsPage;
